import { governanceExactKeys, governanceObject } from './governance-validation.js';
export const ORGANIZATION_GOVERNANCE_AREAS = [
    'ACCOUNTING',
    'JOURNALS',
    'CASH',
    'COMPLIANCE',
    'CLIENTS',
    'PRIVACY',
    'INTEGRATIONS',
    'MONETARY_APPROVALS',
    'RISK',
];
/**
 * Maps each governance area to the module that must be entitled for the area to
 * be visible. A `null` value marks a crosscutting common control that is always
 * shown regardless of module entitlements.
 *
 * Module-gated areas (ACCOUNTING, JOURNALS, CASH, COMPLIANCE, CLIENTS, RISK) are
 * gated by their corresponding entitled module. The UI is always scoped to the
 * active entitled domain/module, even when underlying models are shared.
 * Crosscutting controls (PRIVACY, INTEGRATIONS, MONETARY_APPROVALS) are always
 * present because they apply across every entitled module.
 */
export const ORGANIZATION_GOVERNANCE_AREA_MODULE = Object.freeze({
    ACCOUNTING: 'ACCOUNTING',
    JOURNALS: 'ACCOUNTING',
    CASH: 'RECONCILIATION',
    COMPLIANCE: 'REGULATORY',
    CLIENTS: 'INVESTMENT',
    RISK: 'REPORTING',
    PRIVACY: null,
    INTEGRATIONS: null,
    MONETARY_APPROVALS: null,
});
/**
 * Returns the governance areas visible for the given set of enabled modules.
 * Crosscutting areas (null module) are always included.
 */
export function visibleGovernanceAreas(enabledModules) {
    const enabled = new Set(enabledModules);
    return ORGANIZATION_GOVERNANCE_AREAS.filter((area) => {
        const module = ORGANIZATION_GOVERNANCE_AREA_MODULE[area];
        return module === null || enabled.has(module);
    });
}
const field = (key, kind = 'TEXT', required = true) => ({ key, kind, required });
const definition = (value) => value;
const restatement = {
    required: ['supersedesResultId', 'restatementReason'],
};
const statementRestatement = {
    required: ['supersedesRunId', 'reason'],
};
const agreement = {
    required: ['agreementId', 'clientId', 'templateId', 'version', 'effectiveFrom', 'sourceIds'],
    optional: ['portfolioAccountId', 'expiresAt', 'supersedesAgreementId'],
};
const credential = {
    required: ['fingerprint', 'algorithm', 'verificationKey', 'validFrom'],
};
export const ORGANIZATION_GOVERNANCE_COMMAND_REGISTRY = [
    definition({
        commandId: 'accounting.classification.record',
        area: 'ACCOUNTING',
        method: 'POST',
        path: '/api/organization-governance/accounting/classifications',
        required: [
            'subjectType',
            'subjectId',
            'eventTime',
            'assetClass',
            'classification',
            'measurementBasis',
            'judgmentEvidenceIds',
            'preparedBy',
        ],
        optional: ['restatement'],
        nested: { restatement },
        fields: [
            field('subjectType'),
            field('subjectId'),
            field('eventTime', 'INSTANT'),
            field('assetClass'),
            field('classification'),
            field('measurementBasis'),
            field('judgmentEvidenceIds', 'STRING_LIST'),
            field('preparedBy'),
            field('restatement', 'STRUCTURED', false),
        ],
    }),
    definition({
        commandId: 'accounting.expected-credit-loss.record',
        area: 'ACCOUNTING',
        method: 'POST',
        path: '/api/organization-governance/accounting/expected-credit-loss-runs',
        required: [
            'subjectType',
            'subjectId',
            'modelId',
            'modelVersion',
            'inputEvidenceIds',
            'riskStageBefore',
            'riskStageAfter',
            'completenessCheckId',
            'journalEntryId',
            'exposure',
            'lossRate',
            'eventTime',
            'preparedBy',
        ],
        optional: ['restatement'],
        nested: { restatement },
        fields: [
            field('subjectType'),
            field('subjectId'),
            field('modelId'),
            field('modelVersion'),
            field('inputEvidenceIds', 'STRING_LIST'),
            field('riskStageBefore'),
            field('riskStageAfter'),
            field('completenessCheckId'),
            field('journalEntryId'),
            field('exposure', 'DECIMAL'),
            field('lossRate', 'DECIMAL'),
            field('eventTime', 'INSTANT'),
            field('preparedBy'),
            field('restatement', 'STRUCTURED', false),
        ],
    }),
    definition({
        commandId: 'accounting.fair-value.record',
        area: 'ACCOUNTING',
        method: 'POST',
        path: '/api/organization-governance/accounting/fair-value-measurements',
        required: [
            'subjectType',
            'subjectId',
            'hierarchy',
            'observableInputEvidenceIds',
            'unobservableInputEvidenceIds',
            'uncertaintyDisclosure',
            'eventTime',
            'preparedBy',
        ],
        optional: ['restatement'],
        nested: { restatement },
        fields: [
            field('subjectType'),
            field('subjectId'),
            field('hierarchy'),
            field('observableInputEvidenceIds', 'STRING_LIST'),
            field('unobservableInputEvidenceIds', 'STRING_LIST'),
            field('uncertaintyDisclosure'),
            field('eventTime', 'INSTANT'),
            field('preparedBy'),
            field('restatement', 'STRUCTURED', false),
        ],
    }),
    definition({
        commandId: 'accounting.disclosure.record',
        area: 'ACCOUNTING',
        method: 'POST',
        path: '/api/organization-governance/accounting/disclosures',
        required: [
            'subjectType',
            'subjectId',
            'disclosureVersion',
            'periodEnd',
            'evidenceIds',
            'preparedBy',
        ],
        optional: ['restatement'],
        nested: { restatement },
        fields: [
            field('subjectType'),
            field('subjectId'),
            field('disclosureVersion'),
            field('periodEnd', 'INSTANT'),
            field('evidenceIds', 'STRING_LIST'),
            field('preparedBy'),
            field('restatement', 'STRUCTURED', false),
        ],
    }),
    definition({
        commandId: 'compliance.related-party.declare',
        area: 'COMPLIANCE',
        method: 'POST',
        path: '/api/organization-governance/compliance/related-parties',
        required: ['subjectId', 'relatedPartyId', 'relationshipType', 'sourceIds', 'declaredAt'],
        fields: [
            field('subjectId'),
            field('relatedPartyId'),
            field('relationshipType'),
            field('sourceIds', 'STRING_LIST'),
            field('declaredAt', 'INSTANT'),
        ],
    }),
    definition({
        commandId: 'cash.suspense.create',
        area: 'CASH',
        method: 'POST',
        path: '/api/reconciliation/suspense-cash/cases',
        required: ['externalReference', 'direction', 'currency', 'amount', 'valueDate', 'narrative'],
        optional: ['externalStatementId', 'candidateClientAccountIds'],
        fields: [
            field('externalReference'),
            field('direction'),
            field('currency'),
            field('amount', 'DECIMAL'),
            field('valueDate'),
            field('narrative'),
            field('externalStatementId', 'TEXT', false),
            field('candidateClientAccountIds', 'STRING_LIST', false),
        ],
    }),
    definition({
        commandId: 'cash.suspense.request-resolution',
        area: 'CASH',
        method: 'POST',
        path: '/api/reconciliation/suspense-cash/cases/:caseId/resolution-requests',
        pathParameterKeys: ['caseId'],
        required: [
            'resolutionType',
            'expectedVersion',
            'reason',
            'evidence',
            'cashLedgerAccountId',
            'suspenseLedgerAccountId',
        ],
        optional: ['targetPortfolioAccountId'],
        fields: [
            field('resolutionType'),
            field('expectedVersion', 'INTEGER'),
            field('reason'),
            field('evidence', 'STRING_LIST'),
            field('cashLedgerAccountId'),
            field('suspenseLedgerAccountId'),
            field('targetPortfolioAccountId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'cash.suspense.approve-resolution',
        area: 'CASH',
        method: 'POST',
        path: '/api/reconciliation/suspense-cash/cases/:caseId/resolution-approvals',
        pathParameterKeys: ['caseId'],
        required: ['expectedVersion'],
        optional: ['monetaryApprovalDecisionId'],
        fields: [
            field('expectedVersion', 'INTEGER'),
            field('monetaryApprovalDecisionId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'cash.suspense.request-reopen',
        area: 'CASH',
        method: 'POST',
        path: '/api/reconciliation/suspense-cash/cases/:caseId/reopen-requests',
        pathParameterKeys: ['caseId'],
        required: ['expectedVersion', 'reason'],
        fields: [field('expectedVersion', 'INTEGER'), field('reason')],
    }),
    definition({
        commandId: 'cash.suspense.approve-reopen',
        area: 'CASH',
        method: 'POST',
        path: '/api/reconciliation/suspense-cash/cases/:caseId/reopen-approvals',
        pathParameterKeys: ['caseId'],
        required: ['expectedVersion'],
        fields: [field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'journals.template.create',
        area: 'JOURNALS',
        method: 'POST',
        path: '/api/accounting/journal-templates',
        required: ['templateKey', 'name', 'expectedLatestVersion', 'policy'],
        optional: ['description'],
        fields: [
            field('templateKey'),
            field('name'),
            field('expectedLatestVersion', 'INTEGER'),
            field('policy', 'STRUCTURED'),
            field('description', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'journals.template.approve',
        area: 'JOURNALS',
        method: 'POST',
        path: '/api/accounting/journal-templates/:templateId/approve',
        pathParameterKeys: ['templateId'],
        required: ['expectedVersion'],
        fields: [field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'journals.schedule.create',
        area: 'JOURNALS',
        method: 'POST',
        path: '/api/accounting/journal-schedules',
        required: [
            'scheduleKey',
            'templateId',
            'expectedLatestVersion',
            'effectiveFrom',
            'frequency',
            'interval',
            'dimensions',
        ],
        optional: ['effectiveTo', 'autoReversalDelayDays', 'reference', 'narration'],
        fields: [
            field('scheduleKey'),
            field('templateId'),
            field('expectedLatestVersion', 'INTEGER'),
            field('effectiveFrom'),
            field('frequency'),
            field('interval', 'INTEGER'),
            field('dimensions', 'STRUCTURED'),
            field('effectiveTo', 'TEXT', false),
            field('autoReversalDelayDays', 'INTEGER', false),
            field('reference', 'TEXT', false),
            field('narration', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'journals.schedule.amend',
        area: 'JOURNALS',
        method: 'PATCH',
        path: '/api/accounting/journal-schedules/:scheduleId/amend',
        pathParameterKeys: ['scheduleId'],
        required: [
            'scheduleKey',
            'templateId',
            'expectedLatestVersion',
            'effectiveFrom',
            'frequency',
            'interval',
            'dimensions',
        ],
        optional: ['effectiveTo', 'autoReversalDelayDays', 'reference', 'narration'],
        fields: [
            field('scheduleKey'),
            field('templateId'),
            field('expectedLatestVersion', 'INTEGER'),
            field('effectiveFrom'),
            field('frequency'),
            field('interval', 'INTEGER'),
            field('dimensions', 'STRUCTURED'),
            field('effectiveTo', 'TEXT', false),
            field('autoReversalDelayDays', 'INTEGER', false),
            field('reference', 'TEXT', false),
            field('narration', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'journals.schedule.approve',
        area: 'JOURNALS',
        method: 'POST',
        path: '/api/accounting/journal-schedules/:scheduleId/approve',
        pathParameterKeys: ['scheduleId'],
        required: ['expectedVersion'],
        fields: [field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'journals.schedule.cancel',
        area: 'JOURNALS',
        method: 'POST',
        path: '/api/accounting/journal-schedules/:scheduleId/cancel',
        pathParameterKeys: ['scheduleId'],
        required: ['expectedVersion', 'effectiveDate', 'reason'],
        fields: [field('expectedVersion', 'INTEGER'), field('effectiveDate'), field('reason')],
    }),
    definition({
        commandId: 'compliance.conflict.decide',
        area: 'COMPLIANCE',
        method: 'POST',
        path: '/api/organization-governance/compliance/conflict-decisions',
        required: [
            'actorId',
            'conflictId',
            'recusedAt',
            'reasonSourceIds',
            'declarationId',
            'businessEntityType',
            'businessEntityId',
            'decision',
            'reason',
        ],
        optional: ['materialityAmount', 'currency', 'supersedesDecisionId'],
        fields: [
            field('actorId'),
            field('conflictId'),
            field('recusedAt', 'INSTANT'),
            field('reasonSourceIds', 'STRING_LIST'),
            field('declarationId'),
            field('businessEntityType'),
            field('businessEntityId'),
            field('decision'),
            field('reason'),
            field('materialityAmount', 'DECIMAL', false),
            field('currency', 'TEXT', false),
            field('supersedesDecisionId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'compliance.screening-snapshot.record',
        area: 'COMPLIANCE',
        method: 'POST',
        path: '/api/organization-governance/compliance/screening-list-snapshots',
        required: ['listSourceId', 'snapshotHash', 'retrievedAt'],
        fields: [field('listSourceId'), field('snapshotHash'), field('retrievedAt', 'INSTANT')],
    }),
    definition({
        commandId: 'compliance.screening.decide',
        area: 'COMPLIANCE',
        method: 'POST',
        path: '/api/organization-governance/compliance/screening-decisions',
        required: [
            'snapshotId',
            'subjectId',
            'disposition',
            'evidenceIds',
            'decidedAt',
            'subjectType',
            'alertType',
            'explanation',
        ],
        optional: ['supersedesDecisionId'],
        fields: [
            field('snapshotId'),
            field('subjectId'),
            field('disposition'),
            field('evidenceIds', 'STRING_LIST'),
            field('decidedAt', 'INSTANT'),
            field('subjectType'),
            field('alertType'),
            field('explanation'),
            field('supersedesDecisionId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'compliance.regulatory-report.prepare',
        area: 'COMPLIANCE',
        method: 'POST',
        path: '/api/organization-governance/compliance/regulatory-reports',
        required: ['report'],
        nested: {
            report: {
                required: ['reportType', 'caseId', 'retentionSourceIds'],
                optional: ['correctionOfReportId'],
            },
        },
        fields: [field('report', 'STRUCTURED')],
    }),
    definition({
        commandId: 'compliance.regulatory-report.transition',
        area: 'COMPLIANCE',
        method: 'PATCH',
        path: '/api/organization-governance/compliance/regulatory-reports/:reportId',
        pathParameterKeys: ['reportId'],
        required: ['expectedVersion', 'action'],
        optional: ['reference'],
        fields: [
            field('expectedVersion', 'INTEGER'),
            field('action'),
            field('reference', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'clients.agreement.generate',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/agreements',
        ...agreement,
        fields: [
            field('agreementId'),
            field('clientId'),
            field('templateId'),
            field('version'),
            field('effectiveFrom', 'INSTANT'),
            field('sourceIds', 'STRING_LIST'),
            field('portfolioAccountId', 'TEXT', false),
            field('expiresAt', 'INSTANT', false),
            field('supersedesAgreementId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'clients.agreement.deliver',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/agreements/deliveries',
        required: ['agreementId', 'deliveryId', 'deliveredAt', 'artifactHash', 'expectedVersion'],
        fields: [
            field('agreementId'),
            field('deliveryId'),
            field('deliveredAt', 'INSTANT'),
            field('artifactHash'),
            field('expectedVersion', 'INTEGER'),
        ],
    }),
    definition({
        commandId: 'clients.agreement.decide',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/agreements/decisions',
        required: [
            'agreementId',
            'decision',
            'decidedAt',
            'requiredConsentIds',
            'optionalConsentIds',
            'decidedBy',
            'expectedVersion',
        ],
        optional: ['expiresAt'],
        fields: [
            field('agreementId'),
            field('decision'),
            field('decidedAt', 'INSTANT'),
            field('requiredConsentIds', 'STRING_LIST'),
            field('optionalConsentIds', 'STRING_LIST'),
            field('decidedBy'),
            field('expectedVersion', 'INTEGER'),
            field('expiresAt', 'INSTANT', false),
        ],
    }),
    definition({
        commandId: 'clients.agreement.amend',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/agreements/amendments',
        required: [
            'agreementId',
            'supersedesVersion',
            'effectiveFrom',
            'withdrawnConsentIds',
            'expectedVersion',
            'newAgreement',
        ],
        nested: { newAgreement: agreement },
        fields: [
            field('agreementId'),
            field('supersedesVersion'),
            field('effectiveFrom', 'INSTANT'),
            field('withdrawnConsentIds', 'STRING_LIST'),
            field('expectedVersion', 'INTEGER'),
            field('newAgreement', 'STRUCTURED'),
        ],
    }),
    definition({
        commandId: 'clients.statement.generate',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statements/generations',
        required: [
            'reportRunId',
            'clientId',
            'periodStart',
            'periodEnd',
            'cutoffAt',
            'generationVersion',
        ],
        optional: ['portfolioAccountId'],
        fields: [
            field('reportRunId'),
            field('clientId'),
            field('portfolioAccountId', 'TEXT', false),
            field('periodStart', 'INSTANT'),
            field('periodEnd', 'INSTANT'),
            field('cutoffAt', 'INSTANT'),
            field('generationVersion', 'INTEGER'),
        ],
    }),
    definition({
        commandId: 'clients.statement-schedule.create',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statement-schedules',
        required: [
            'scheduleId',
            'scheduleKey',
            'expectedLatestVersion',
            'clientId',
            'generationVersion',
            'periods',
        ],
        optional: ['portfolioAccountId'],
        fields: [
            field('scheduleId'),
            field('scheduleKey'),
            field('expectedLatestVersion', 'INTEGER'),
            field('clientId'),
            field('portfolioAccountId', 'TEXT', false),
            field('generationVersion', 'INTEGER'),
            field('periods', 'STRUCTURED_LIST'),
        ],
    }),
    definition({
        commandId: 'clients.statement-schedule.approve',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statement-schedules/:scheduleId/approvals',
        pathParameterKeys: ['scheduleId'],
        required: ['expectedRevision'],
        fields: [field('expectedRevision', 'INTEGER')],
    }),
    definition({
        commandId: 'clients.statement-schedule.cancel',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statement-schedules/:scheduleId/cancellations',
        pathParameterKeys: ['scheduleId'],
        required: ['expectedRevision', 'reason'],
        fields: [field('expectedRevision', 'INTEGER'), field('reason')],
    }),
    definition({
        commandId: 'clients.statement.create',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statements',
        required: [
            'runId',
            'statementVersion',
            'artifactHash',
            'inputFingerprint',
            'reportRunId',
            'clientId',
            'periodStart',
            'periodEnd',
        ],
        optional: ['portfolioAccountId', 'restatement'],
        nested: { restatement: statementRestatement },
        fields: [
            field('runId'),
            field('statementVersion'),
            field('artifactHash'),
            field('inputFingerprint'),
            field('reportRunId'),
            field('clientId'),
            field('periodStart', 'INSTANT'),
            field('periodEnd', 'INSTANT'),
            field('portfolioAccountId', 'TEXT', false),
            field('restatement', 'STRUCTURED', false),
        ],
    }),
    definition({
        commandId: 'clients.statement-delivery.record',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/statements/delivery-attempts',
        required: ['statementRunId', 'attemptId', 'status', 'attemptedAt', 'artifactHash', 'channel'],
        optional: ['remediationId', 'reasonCode'],
        fields: [
            field('statementRunId'),
            field('attemptId'),
            field('status'),
            field('attemptedAt', 'INSTANT'),
            field('artifactHash'),
            field('channel'),
            field('remediationId', 'TEXT', false),
            field('reasonCode', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'clients.complaint.intake',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/complaints',
        required: [
            'complaintId',
            'classification',
            'acknowledgedAt',
            'ownerId',
            'slaPolicyId',
            'evidenceIds',
            'clientId',
        ],
        fields: [
            field('complaintId'),
            field('classification'),
            field('acknowledgedAt', 'INSTANT'),
            field('ownerId'),
            field('slaPolicyId'),
            field('evidenceIds', 'STRING_LIST'),
            field('clientId'),
        ],
    }),
    definition({
        commandId: 'clients.complaint.act',
        area: 'CLIENTS',
        method: 'POST',
        path: '/api/organization-governance/clients/complaints/actions',
        required: ['type', 'payload', 'expectedVersion'],
        fields: [field('type'), field('payload', 'STRUCTURED'), field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'privacy.request.intake',
        area: 'PRIVACY',
        method: 'POST',
        path: '/api/organization-governance/privacy/requests',
        required: [
            'requestId',
            'requestType',
            'requesterUserIdentityId',
            'subjectUserIdentityId',
            'receivedAt',
            'identityEvidenceHash',
            'identityEvidenceType',
            'requestedCategoryIds',
            'idempotencyKey',
        ],
        fields: [
            field('requestId'),
            field('requestType'),
            field('requesterUserIdentityId'),
            field('subjectUserIdentityId'),
            field('receivedAt', 'INSTANT'),
            field('identityEvidenceHash'),
            field('identityEvidenceType'),
            field('requestedCategoryIds', 'STRING_LIST'),
            field('idempotencyKey'),
        ],
    }),
    definition({
        commandId: 'privacy.request.decide',
        area: 'PRIVACY',
        method: 'POST',
        path: '/api/organization-governance/privacy/decisions',
        required: [
            'requestId',
            'decision',
            'decidedAt',
            'reason',
            'retainedCategoryIds',
            'legalHoldIds',
            'correctionLineageIds',
            'expectedVersion',
        ],
        optional: ['optionalPurposeIds'],
        fields: [
            field('requestId'),
            field('decision'),
            field('decidedAt', 'INSTANT'),
            field('reason'),
            field('retainedCategoryIds', 'STRING_LIST'),
            field('legalHoldIds', 'STRING_LIST'),
            field('correctionLineageIds', 'STRING_LIST'),
            field('expectedVersion', 'INTEGER'),
            field('optionalPurposeIds', 'STRING_LIST', false),
        ],
    }),
    definition({
        commandId: 'integrations.connection.configure',
        area: 'INTEGRATIONS',
        method: 'POST',
        path: '/api/organization-governance/integrations',
        required: ['connectionCode', 'providerType', 'credential'],
        nested: { credential },
        fields: [field('connectionCode'), field('providerType'), field('credential', 'STRUCTURED')],
    }),
    definition({
        commandId: 'integrations.credential.rotate',
        area: 'INTEGRATIONS',
        method: 'POST',
        path: '/api/organization-governance/integrations/:connectionId/credential-rotations',
        pathParameterKeys: ['connectionId'],
        required: ['expectedVersion', 'credential'],
        nested: { credential },
        fields: [field('expectedVersion', 'INTEGER'), field('credential', 'STRUCTURED')],
    }),
    definition({
        commandId: 'integrations.credential.approve',
        area: 'INTEGRATIONS',
        method: 'POST',
        path: '/api/organization-governance/integrations/:connectionId/credentials/:credentialId/approvals',
        pathParameterKeys: ['connectionId', 'credentialId'],
        required: ['expectedVersion'],
        fields: [field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'integrations.message.receive',
        area: 'INTEGRATIONS',
        method: 'POST',
        path: '/api/organization-governance/integrations/messages',
        required: [
            'connectionId',
            'messageId',
            'streamId',
            'sequence',
            'occurredAt',
            'keyFingerprint',
            'signatureAlgorithm',
            'payloadHash',
            'signature',
        ],
        fields: [
            field('connectionId'),
            field('messageId'),
            field('streamId'),
            field('sequence'),
            field('occurredAt', 'INSTANT'),
            field('keyFingerprint'),
            field('signatureAlgorithm'),
            field('payloadHash'),
            field('signature'),
        ],
    }),
    definition({
        commandId: 'integrations.message.commit',
        area: 'INTEGRATIONS',
        method: 'POST',
        path: '/api/organization-governance/integrations/messages/:receiptId/commits',
        pathParameterKeys: ['receiptId'],
        required: ['committedEntityType', 'committedEntityId'],
        fields: [field('committedEntityType'), field('committedEntityId')],
    }),
    definition({
        commandId: 'monetary.authority.create',
        area: 'MONETARY_APPROVALS',
        method: 'POST',
        path: '/api/organization-governance/monetary-approvals/authorities',
        required: [
            'userIdentityId',
            'tierId',
            'actionIds',
            'maximumBaseAmount',
            'effectiveFrom',
            'reason',
        ],
        optional: ['effectiveTo', 'delegatedFromId'],
        fields: [
            field('userIdentityId'),
            field('tierId'),
            field('actionIds', 'STRING_LIST'),
            field('maximumBaseAmount', 'DECIMAL'),
            field('effectiveFrom', 'INSTANT'),
            field('reason'),
            field('effectiveTo', 'INSTANT', false),
            field('delegatedFromId', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'monetary.authority.approve',
        area: 'MONETARY_APPROVALS',
        method: 'POST',
        path: '/api/organization-governance/monetary-approvals/authorities/:authorityId/approvals',
        pathParameterKeys: ['authorityId'],
        required: ['expectedVersion'],
        fields: [field('expectedVersion', 'INTEGER')],
    }),
    definition({
        commandId: 'monetary.authority.revoke',
        area: 'MONETARY_APPROVALS',
        method: 'POST',
        path: '/api/organization-governance/monetary-approvals/authorities/:authorityId/revocations',
        pathParameterKeys: ['authorityId'],
        required: ['expectedVersion', 'reason'],
        fields: [field('expectedVersion', 'INTEGER'), field('reason')],
    }),
    definition({
        commandId: 'monetary.approval.evaluate',
        area: 'MONETARY_APPROVALS',
        method: 'POST',
        path: '/api/organization-governance/monetary-approvals/decisions',
        required: [
            'actionId',
            'businessIdentity',
            'amount',
            'currency',
            'baseAmount',
            'fxObservationId',
            'fxObservedAt',
            'actorId',
            'relatedBusinessIdentityIds',
            'eventTime',
        ],
        fields: [
            field('actionId'),
            field('businessIdentity'),
            field('amount', 'DECIMAL'),
            field('currency'),
            field('baseAmount', 'DECIMAL'),
            field('fxObservationId'),
            field('fxObservedAt', 'INSTANT'),
            field('actorId'),
            field('relatedBusinessIdentityIds', 'STRING_LIST'),
            field('eventTime', 'INSTANT'),
        ],
    }),
    definition({
        commandId: 'risk.analysis.run',
        area: 'RISK',
        method: 'POST',
        path: '/api/organization-governance/risk/runs',
        required: ['portfolioAccountId', 'cutoffAt'],
        optional: ['supersedesRunId', 'restatementReason'],
        fields: [
            field('portfolioAccountId'),
            field('cutoffAt', 'INSTANT'),
            field('supersedesRunId', 'TEXT', false),
            field('restatementReason', 'TEXT', false),
        ],
    }),
    definition({
        commandId: 'risk.breach.transition',
        area: 'RISK',
        method: 'POST',
        path: '/api/organization-governance/risk/breaches/:breachId/transitions',
        pathParameterKeys: ['breachId'],
        required: ['expectedVersion', 'action', 'reason'],
        optional: ['resolutionRunId'],
        fields: [
            field('expectedVersion', 'INTEGER'),
            field('action'),
            field('reason'),
            field('resolutionRunId', 'TEXT', false),
        ],
    }),
];
const registryById = new Map(ORGANIZATION_GOVERNANCE_COMMAND_REGISTRY.map((entry) => [entry.commandId, entry]));
function validateExactObject(value, schema) {
    if (!governanceExactKeys(value, schema.required, schema.optional))
        return false;
    if (!schema.nested)
        return true;
    return Object.entries(schema.nested).every(([key, nested]) => {
        if (!Object.hasOwn(value, key))
            return true;
        return validateExactObject(value[key], nested);
    });
}
export function validateOrganizationGovernanceCommandBody(commandId, value) {
    const definition = registryById.get(commandId);
    return Boolean(definition && governanceObject(value) && validateExactObject(value, definition));
}
function jsonSchemaForExactObject(schema) {
    return {
        type: 'object',
        additionalProperties: false,
        required: schema.required,
        properties: Object.fromEntries([...schema.required, ...(schema.optional ?? [])].map((key) => [
            key,
            schema.nested?.[key] ? jsonSchemaForExactObject(schema.nested[key]) : {},
        ])),
    };
}
export function organizationGovernanceCommandJsonSchema(commandId) {
    const definition = registryById.get(commandId);
    if (!definition)
        throw new Error(`Unknown organization governance command: ${commandId}`);
    const properties = Object.fromEntries(definition.fields.map((entry) => {
        const nested = definition.nested?.[entry.key];
        if (nested)
            return [entry.key, jsonSchemaForExactObject(nested)];
        if (entry.kind === 'INTEGER')
            return [entry.key, { type: 'integer' }];
        if (entry.kind === 'STRING_LIST')
            return [entry.key, { type: 'array', items: { type: 'string' }, maxItems: 128 }];
        if (entry.kind === 'STRUCTURED_LIST')
            return [
                entry.key,
                { type: 'array', items: { type: 'object' }, minItems: 1, maxItems: 120 },
            ];
        if (entry.kind === 'STRUCTURED')
            return [entry.key, { type: 'object' }];
        return [entry.key, { type: 'string' }];
    }));
    return {
        type: 'object',
        additionalProperties: false,
        required: definition.required,
        properties,
    };
}
export const ORGANIZATION_GOVERNANCE_READ_RESOURCES = [
    { area: 'ACCOUNTING', path: '/api/organization-governance/accounting/results' },
    { area: 'JOURNALS', path: '/api/accounting/journal-templates' },
    { area: 'JOURNALS', path: '/api/accounting/journal-schedules' },
    { area: 'CASH', path: '/api/reconciliation/suspense-cash/cases' },
    { area: 'COMPLIANCE', path: '/api/organization-governance/compliance/related-parties' },
    { area: 'CLIENTS', path: '/api/organization-governance/clients/agreements' },
    { area: 'CLIENTS', path: '/api/organization-governance/clients/statements' },
    { area: 'CLIENTS', path: '/api/organization-governance/clients/statement-schedules' },
    { area: 'CLIENTS', path: '/api/organization-governance/clients/complaints' },
    { area: 'PRIVACY', path: '/api/organization-governance/privacy/requests' },
    { area: 'INTEGRATIONS', path: '/api/organization-governance/integrations' },
    { area: 'MONETARY_APPROVALS', path: '/api/organization-governance/monetary-approvals' },
    { area: 'RISK', path: '/api/organization-governance/risk/runs' },
];
